/**
 * Running a compiled filter against one item.
 *
 * ## The boundary this module must never cross
 *
 * A filter is **presentation only**. `evaluateItem` decides whether the player is
 * shown a label and a beam, in what colour, at what size, with which sound — and
 * nothing else. It does not decide what exists, what dropped, or what may be taken.
 * `CMD_PICKUP` is re-checked server-side for the item's existence, the player's
 * liveness, the range between them and the space left in the bag, exactly as it is for
 * a click. A `Hide` rule hides a label; the item is still lying on that floor and the
 * player can still walk over and click it.
 *
 * That extends to auto-pickup. `autoPickupTargets` returns **ids**, and that is the
 * whole of its job: no socket, no command, no side effect. The caller sends the same
 * `CMD_PICKUP` a click sends, through the same path, at the same rate limit — a vacuum
 * that emptied a room faster than a human could click would be the client deciding an
 * outcome, and the server would be right to refuse it.
 *
 * Pure: no Babylon, no Solid, no DOM, no clock, no randomness.
 *
 * ## Missing data never passes
 *
 * The two item shapes the client holds carry different fields. A `GroundItem` from the
 * snapshot has a name and a rarity; a carried `InventoryItem` also has an item level,
 * a base id, a slot and its affixes. A condition about a field the item does not carry
 * evaluates to **false** — the rule does not match. Treating absence as a pass would
 * make `ItemLevel >= 85` show every ground drop in the game, which is the opposite of
 * what the line says. `groundBlindConditions` exists so an editor can point out the
 * lines that can never fire on a floor drop, rather than leaving the player to work it
 * out from an empty screen.
 */

import {
  RARITY_ORDER,
  type GroundItem,
  type InventoryItem,
  type ItemRarity,
} from '../../net/protocol';
import type {
  CompareOp,
  CompiledFilter,
  Condition,
  FilterRule,
  FilterStyle,
  FilterTier,
} from './parse';

export type { FilterStyle, FilterTier };

/**
 * An item as the filter sees it.
 *
 * A shape of its own rather than one of the wire types, because a filter has to run
 * over both — ground drops for labels and beams, carried items for the vault's
 * highlighting — and only the fields that are genuinely optional on the wire are
 * optional here. Build one with `groundFilterItem` or `inventoryFilterItem`.
 */
export interface FilterItem {
  /** Loot id for a ground drop, uuid for a carried one. Only used for tracing. */
  id: string;
  name: string;
  rarity: ItemRarity;
  /** Absent on ground drops — the snapshot does not carry it. */
  itemLevel?: number;
  /** Catalogue base id, e.g. "short_sword". Absent on ground drops. */
  baseId?: string;
  /** Paper-doll slot. Absent on ground drops. */
  slot?: string;
  /** Rolled affixes. Absent on ground drops; an empty array means a plain item. */
  affixes?: readonly { stat: string; value: number }[];
}

/** What the filter knows about where the player is standing. */
export interface FilterContext {
  /**
   * The current zone's enemy level, for `AreaLevel`. Absent in town, where there is
   * no enemy level to speak of — so an `AreaLevel` condition never matches in the hub,
   * which is the honest reading of "this zone is level N".
   */
  areaLevel?: number;
}

/** What the HUD should do with one item. */
export interface FilterVerdict {
  /** Whether to draw a label and a beam at all. */
  visible: boolean;
  /** Priority tier 1-4, driving colour, beam size and sound. */
  tier: FilterTier;
  /** Resolved styling. Empty when nothing asked for any. */
  style: FilterStyle;
  /** Whether the vacuum key should sweep this item up. */
  autoPickup: boolean;
  /**
   * Rules that matched, in evaluation order. Usually one; more than one only when a
   * matching rule said `Continue`. Empty when the built-in default decided.
   */
  matched: readonly FilterRule[];
  /** Whether a rule decided, or the built-in fallback did. */
  source: 'RULE' | 'DEFAULT';
}

/**
 * The rarity floor the built-in fallback shows, straight from the spec's
 * `evaluateItemDrop`: an item no rule mentions is shown if it is Rare or better.
 *
 * The fallback is deliberately *not* "show everything". A filter is loaded to reduce
 * clutter, and a rule the player forgot to write should not undo that.
 */
export const DEFAULT_MIN_RARITY: ItemRarity = 'RARE';

/**
 * Tier for an item no rule assigned one to, from the spec's tier table: uniques are
 * Tier 1, rare and magic gear is Tier 3, plain items are Tier 4.
 *
 * **Tier 2 has no automatic case, on purpose.** Every Tier 2 example in the spec — a
 * six-socket white base, a mid rune, a top-tier gem — is content GRIND does not have,
 * so deriving Tier 2 from anything would mean inventing a rule for "high value" that
 * no design document states. It is reachable only through `SetTier 2`, where it is the
 * player's judgement rather than a guess of ours.
 */
export function defaultTierFor(rarity: ItemRarity): FilterTier {
  switch (rarity) {
    case 'UNIQUE':
      return 1;
    case 'RARE':
    case 'MAGIC':
      return 3;
    default:
      return 4;
  }
}

/** Ascending rarity index. -1 for anything off the four-tier ladder. */
function rarityRank(rarity: ItemRarity): number {
  return RARITY_ORDER.indexOf(rarity);
}

function compare(op: CompareOp, left: number, right: number): boolean {
  switch (op) {
    case '==':
      return left === right;
    case '!=':
      return left !== right;
    case '<':
      return left < right;
    case '<=':
      return left <= right;
    case '>':
      return left > right;
    case '>=':
      return left >= right;
  }
}

/** Whether one condition holds. Absent data is always a non-match — see the header. */
function holds(condition: Condition, item: FilterItem, ctx: FilterContext): boolean {
  switch (condition.kind) {
    case 'RARITY_CMP':
      return compare(condition.op, rarityRank(item.rarity), rarityRank(condition.rarity));

    case 'RARITY_IN':
      return condition.rarities.includes(item.rarity);

    case 'ITEM_LEVEL':
      return item.itemLevel !== undefined && compare(condition.op, item.itemLevel, condition.value);

    case 'AREA_LEVEL':
      return ctx.areaLevel !== undefined && compare(condition.op, ctx.areaLevel, condition.value);

    case 'AFFIX_COUNT':
      return (
        item.affixes !== undefined && compare(condition.op, item.affixes.length, condition.value)
      );

    case 'NAME': {
      const name = item.name.toLowerCase();
      return condition.needles.some((needle) => name.includes(needle));
    }

    case 'BASE_TYPE': {
      if (item.baseId === undefined) return false;
      const base = item.baseId.toLowerCase();
      return condition.needles.some((needle) => base.includes(needle));
    }

    case 'CLASS':
      return item.slot !== undefined && (condition.slots as readonly string[]).includes(item.slot);

    case 'STAT': {
      if (item.affixes === undefined) return false;
      // One affix with this key satisfying the comparison is a match: an item can
      // carry the same stat twice, as a prefix and a suffix, and a player writing
      // `>= 10` means "has one at least that big", not "both of them are".
      return item.affixes.some(
        (a) => a.stat === condition.stat && compare(condition.op, a.value, condition.value),
      );
    }
  }
}

/** Whether every condition in a rule holds. A rule with none matches everything. */
export function ruleMatches(rule: FilterRule, item: FilterItem, ctx: FilterContext = {}): boolean {
  return rule.conditions.every((condition) => holds(condition, item, ctx));
}

/**
 * Runs the filter over one item.
 *
 * Rules are tried in source order and the **first** match decides, exactly as the
 * spec's `evaluateItemDrop` does — which is also what every filter in the genre does,
 * so a player's instinct about putting their important rules at the top is correct.
 *
 * `Continue` is the one wrinkle: a matching rule that carries it contributes its
 * action, tier, styling and pickup flag and then lets evaluation carry on, so a later
 * matching rule overrides what it set. Without any `Continue` in the file this is
 * indistinguishable from first-match-wins.
 */
export function evaluateItem(
  item: FilterItem,
  filter: CompiledFilter,
  ctx: FilterContext = {},
): FilterVerdict {
  const matched: FilterRule[] = [];
  let action: 'SHOW' | 'HIDE' | null = null;
  let tier: FilterTier | null = null;
  let autoPickup: boolean | null = null;
  const style: FilterStyle = {};

  for (const rule of filter.rules) {
    if (!ruleMatches(rule, item, ctx)) continue;

    matched.push(rule);
    action = rule.action;
    if (rule.tier !== undefined) tier = rule.tier;
    if (rule.autoPickup !== undefined) autoPickup = rule.autoPickup;
    // Merged rather than replaced, so a `Continue` rule that only sets a border does
    // not wipe the font size an earlier one chose.
    Object.assign(style, rule.style);

    if (!rule.continues) break;
  }

  if (action === null) {
    return {
      visible: rarityRank(item.rarity) >= rarityRank(DEFAULT_MIN_RARITY),
      tier: defaultTierFor(item.rarity),
      style: {},
      autoPickup: false,
      matched: [],
      source: 'DEFAULT',
    };
  }

  const visible = action === 'SHOW';
  const resolvedTier = tier ?? defaultTierFor(item.rarity);

  return {
    visible,
    tier: resolvedTier,
    style,
    // An explicit `AutoPickup` is an instruction and is obeyed either way — a filter
    // may legitimately want a drop swept up without a label for it. Otherwise the
    // spec's rule applies: the vacuum takes visible Tier 1 and Tier 2 items.
    autoPickup: autoPickup ?? (visible && resolvedTier <= VACUUM_TIER_CEILING),
    matched,
    source: 'RULE',
  };
}

/** Adapts a ground drop. The snapshot carries no item level, base or affixes. */
export function groundFilterItem(g: GroundItem): FilterItem {
  return { id: g.id, name: g.name, rarity: g.rarity };
}

/** Adapts a carried item. Everything the language can ask about is present. */
export function inventoryFilterItem(item: InventoryItem): FilterItem {
  return {
    id: item.uuid,
    name: item.name,
    rarity: item.rarity,
    itemLevel: item.itemLevel,
    baseId: item.baseId,
    slot: item.slot,
    affixes: item.affixes.map((a) => ({ stat: a.stat, value: a.value })),
  };
}

// ── The vacuum key ──────────────────────────────────────────────────────────

/**
 * Worst tier the vacuum sweeps by default: Tier 1 and Tier 2, per the spec's
 * "picks up all visible Tier 1 and Tier 2 filter items".
 */
export const VACUUM_TIER_CEILING: FilterTier = 2;

/**
 * How far the vacuum reaches, in world units — the spec's "5-meter radius".
 *
 * One world unit is one metre everywhere else in this client, so the number is the
 * spec's unchanged. The server's own pickup range is separate and is the one that
 * actually decides: if it is shorter than this, some of these ids will be refused,
 * which is correct behaviour and not a bug to paper over here.
 */
export const VACUUM_RADIUS = 5;

/** Where the player is, for a radius test. */
export interface Point2 {
  x: number;
  z: number;
}

export interface VacuumOptions {
  radius?: number;
  context?: FilterContext;
}

/**
 * Which ground drops a vacuum keypress should ask for, nearest first.
 *
 * Returns ids and nothing else. The caller sends one `CMD_PICKUP` per id through the
 * same path a click uses and at the same rate limit — see the module header. Nearest
 * first because that is the order a player would click in, and because a rate-limited
 * caller that gets cut off should have taken the closest things.
 *
 * Ties break on id so the order is total and the function is deterministic: two drops
 * at the same distance must not swap places between frames.
 */
export function autoPickupTargets(
  ground: readonly GroundItem[],
  filter: CompiledFilter,
  origin: Point2,
  options: VacuumOptions = {},
): string[] {
  const radius = options.radius ?? VACUUM_RADIUS;
  const ctx = options.context ?? {};
  const limit = radius * radius;

  const candidates: { id: string; distance: number }[] = [];
  for (const g of ground) {
    const dx = g.x - origin.x;
    const dz = g.z - origin.z;
    const distance = dx * dx + dz * dz;
    if (distance > limit) continue;
    if (!evaluateItem(groundFilterItem(g), filter, ctx).autoPickup) continue;
    candidates.push({ id: g.id, distance });
  }

  candidates.sort((a, b) => a.distance - b.distance || a.id.localeCompare(b.id));
  return candidates.map((c) => c.id);
}

// ── Warning about rules that cannot fire ────────────────────────────────────

/**
 * Condition kinds a ground drop cannot answer, with the reason.
 *
 * The snapshot's `GroundItem` is deliberately small — it is broadcast to everyone,
 * every tick — so these fields only exist once an item is in the bag.
 */
const GROUND_BLIND: Partial<Record<Condition['kind'], string>> = {
  ITEM_LEVEL: 'the snapshot does not carry an item level for a drop on the floor',
  AFFIX_COUNT: 'the snapshot does not carry affixes for a drop on the floor',
  STAT: 'the snapshot does not carry affixes for a drop on the floor',
  BASE_TYPE: 'the snapshot does not carry a base id for a drop on the floor',
  CLASS: 'the snapshot does not carry an equipment slot for a drop on the floor',
};

/** One condition that can never match a ground drop, and why. */
export interface BlindCondition {
  /** Source line of the condition, so the editor can point at it. */
  line: number;
  kind: Condition['kind'];
  message: string;
}

/**
 * Conditions in this filter that can never match a floor drop.
 *
 * Not an error: the same filter also runs over carried items in the vault, where every
 * one of these works. But a rule that will never light a beam is exactly the kind of
 * silent nothing this module refuses to ship, so the information is available for an
 * editor to show — and it is a warning rather than a rejection because only the caller
 * knows which side it is filtering.
 */
export function groundBlindConditions(filter: CompiledFilter): BlindCondition[] {
  const out: BlindCondition[] = [];
  for (const rule of filter.rules) {
    for (const condition of rule.conditions) {
      const message = GROUND_BLIND[condition.kind];
      if (message) out.push({ line: condition.line, kind: condition.kind, message });
    }
  }
  return out;
}
